import DOMPurify from 'dompurify';
import { IStrapiResponse, IWidget } from '@/Models';
import { getBankingDetailsUrl } from '@/Utils/urls';

export interface WithdrawalMethod {
  id: string;
  name: string;
  priority: number;
  icon: string;
  iconDarkTheme: string;
  description: string;
  bodyHtml: string;
  bodyWithoutWrappers: string;
  available: boolean;
  children?: WithdrawalMethodChild[];
  withdrawalOptionId?: string | number | null;
}

export interface WithdrawalMethodChild {
  id: string;
  name: string;
  priority?: number;
  icon: string;
  iconDarkTheme: string;
  description: string;
  bodyHtml: string;
  bodyWithoutWrappers: string;
  withdrawalOptionId?: string | number | null;
}

export interface WithdrawalBank {
  id: string;
  bankId: number;
  name: string;
  icon: string;
  iconDarkTheme: string;
}

/**
 * Extract withdrawal methods from Strapi BankIcons widget
 */
export const extractWithdrawalMethods = (
  strapiData: IStrapiResponse
): WithdrawalMethod[] => {
  const bankIconsWidget = strapiData.widgets.find(
    (w) => w.name === 'BankIcons'
  );

  if (!bankIconsWidget) return [];

  const bankIconsData = bankIconsWidget.data.find(
    (d) => d.key === 'BankIcons'
  );

  if (!bankIconsData || !Array.isArray(bankIconsData.value)) return [];

  return bankIconsData.value
    .map((item: any) => ({
      id: generateId(item.name),
      name: item.name,
      priority: item.priority,
      icon: item.icon,
      iconDarkTheme: item.iconDarkTheme ?? '',
      description: htmlToText(item.body || ''),
      bodyHtml: sanitizeHtml(item.body || ''),
      bodyWithoutWrappers: stripWrapperTags(item.body || ''),
      available: true,
      withdrawalOptionId: item.withdrawalOptionId ?? item.withdrawalOptionID ?? null,
      children: item.children?.map((child: any) => ({
        id: generateId(child.name),
        name: child.name,
        priority: child.priority,
        icon: child.icon,
        iconDarkTheme: child.iconDarkTheme ?? '',
        description: htmlToText(child.body || ''),
        bodyHtml: sanitizeHtml(child.body || ''),
        bodyWithoutWrappers: stripWrapperTags(child.body || ''),
        withdrawalOptionId: child.withdrawalOptionId ?? child.withdrawalOptionID ?? null,
      })).sort((a: WithdrawalMethodChild, b: WithdrawalMethodChild) => (a.priority ?? 0) - (b.priority ?? 0)),
    }))
    .sort((a, b) => a.priority - b.priority);
};

/**
 * Extract EFT bank metadata from Strapi WithdrawalBanks widget.
 */
export const extractWithdrawalBanks = (
  strapiData: IStrapiResponse
): WithdrawalBank[] => {
  const withdrawalBanksWidget = strapiData.widgets.find(
    (w) => w.name === 'WithdrawalBanks'
  );

  if (!withdrawalBanksWidget) return [];

  const withdrawalBanksData = withdrawalBanksWidget.data.find(
    (d) => d.key === 'WithdrawalBanks'
  );

  if (!withdrawalBanksData || !Array.isArray(withdrawalBanksData.value)) return [];

  return withdrawalBanksData.value
    .map((item: any) => {
      const bankId = Number(item.bankId ?? item.bankID ?? item.BankID);
      const name = String(item.name ?? item.bankName ?? item.BankName ?? '').trim();

      return {
        id: generateId(name || `${bankId}`),
        bankId,
        name,
        icon: item.icon ?? item.Icon ?? '',
        iconDarkTheme: item.iconDarkTheme ?? item.IconDarkTheme ?? '',
      };
    })
    .filter((bank) => Number.isFinite(bank.bankId))
    .sort((a, b) => a.name.localeCompare(b.name));
};

/**
 * Extract turnover threshold text from Strapi
 */
export const extractTurnoverText = (strapiData: IStrapiResponse): string => {
  const turnoverWidget = strapiData.widgets.find(
    (w) => w.name === 'TurnoverThresholdText'
  );

  if (!turnoverWidget) return '';

  const bodyData = turnoverWidget.data.find((d) => d.key === 'Body');

  const value = bodyData?.value;

  return typeof value === 'string' ? value.trim() : '';
};

/**
 * Get withdrawal method by ID
 */
export const getWithdrawalMethodById = (
  strapiData: IStrapiResponse,
  methodId: string
): WithdrawalMethod | undefined => {
  const methods = extractWithdrawalMethods(strapiData);

  return methods.find((method) => method.id === methodId);
};

/**
 * Get widget by name
 */
export const getWidgetByName = (
  strapiData: IStrapiResponse,
  widgetName: string
): IWidget | undefined => {
  return strapiData.widgets.find((w) => w.name === widgetName);
};

/**
 * Generate consistent ID from name
 */
const generateId = (name: string): string => {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '')
    .replace(/\s+/g, '');
};

/**
 * Sanitize HTML safely using DOMPurify
 */
const sanitizeHtml = (html: string): string => {
  if (!html) return '';

  return DOMPurify.sanitize(html);
};

/**
 * Convert HTML to plain text safely
 */
const htmlToText = (html: string): string => {
  if (!html) return '';

  const sanitized = DOMPurify.sanitize(html, { ALLOWED_TAGS: [] });

  const parser = new DOMParser();
  const doc = parser.parseFromString(sanitized, 'text/html');

  return doc.body.textContent?.trim() ?? '';
};

/**
 * Remove wrapper div tags but preserve inner HTML formatting
 */
const stripWrapperTags = (html: string): string => {
  if (!html) return '';

  const sanitized = DOMPurify.sanitize(html);

  const parser = new DOMParser();
  const doc = parser.parseFromString(sanitized, 'text/html');

  const firstElement = doc.body.firstElementChild;

  if (firstElement && firstElement.tagName === 'DIV') {
    firstElement.replaceWith(...Array.from(firstElement.childNodes));
  }

  return doc.body.innerHTML.trim();
};

/**
 * Fix retailers link in HTML content
 * Replaces relative or broken retailers links with proper URL
 */
export const fixRetailersLink = (html: string, premiumSourceUrl: string): string => {
  if (!html) return '';

  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  // Find all anchor tags
  const links = doc.querySelectorAll('a');

  links.forEach((link) => {
    const text = link.textContent?.toLowerCase() || '';

    // Check if this is the retailers link
    if (text.includes('retailer')) {
      // Set the correct URL - same as Instant Money
      const correctUrl = `${premiumSourceUrl}/content/info-help?contentName=cash-send-terms-and-conditions`;
      link.setAttribute('href', correctUrl);
      // Open in new tab like the working BankDetails implementation
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
      link.style.textDecoration = 'underline';
    }
  });

  return doc.body.innerHTML;
};

/**
 * Fix banking details links in Strapi HTML content.
 * Root-relative links resolve against the iframe URL by default, so force them
 * to the premium source site instead.
 */
export const fixBankingDetailsLink = (
  html: string,
  premiumSourceUrl: string | null | undefined
): string => {
  if (!html) return '';

  const bankingDetailsPath = '/account/my-profile/my-banking-details';
  const bankingDetailsUrl = getBankingDetailsUrl(premiumSourceUrl);
  const parser = new DOMParser();
  const doc = parser.parseFromString(html, 'text/html');

  doc.querySelectorAll('a').forEach((link) => {
    const href = link.getAttribute('href');

    if (!href) return;

    const normalizedHref = href.trim();
    const isBankingDetailsLink =
      normalizedHref === bankingDetailsPath ||
      normalizedHref.endsWith(bankingDetailsPath);

    if (isBankingDetailsLink) {
      link.setAttribute('href', bankingDetailsUrl);
      link.setAttribute('target', '_blank');
      link.setAttribute('rel', 'noopener noreferrer');
    }
  });

  return doc.body.innerHTML;
};
